import { Text } from "../../../../../../../../mads/Text";
import { MarkupComponents } from "../../../../../../../../shared/components/MarkupComponents/MarkupComponents";
import { useI18n } from "../../../../../../../../shared/I18n";
import { useViewjobOneGraphModel } from "../../../../../../context/ViewJobDataContext";
import { useLogging } from "../../../context/useLogging";
import { ProfileLink } from "../ProfileLink";

import React, { type ReactNode, useCallback } from "react";
import { Platform } from "react-native";

export type MarkupWrapperProps = {
  label: string;
  linkAriaLabel?: string;
  rtl?: boolean;
  sx?: any;
};

export const MarkupWrapper: React.FunctionComponent<MarkupWrapperProps> = ({
  linkAriaLabel,
  label,
  sx,
}) => {
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const profileBaseUrl = viewjobOneGraphModel?.jobSeeker?.profile?.profileBaseUrl ?? "";

  const { gettext } = useI18n();
  const logger = useLogging();

  const LinkMarkup = useCallback(
    (children: ReactNode) => {
      // On web the link opens the profile in a new tab; on native it pushes an
      // in-app Profile screen onto View Job's stack (RN-4845), so the "opens in a
      // new window" wording only applies on web.
      const defaultAriaLabel =
        Platform.OS === "web" ? gettext("profile (opens in a new window)") : gettext("profile");
      return (
        <ProfileLink
          onPress={() => logger.logProfileNavClick()}
          targetUrl={profileBaseUrl}
          aria-label={linkAriaLabel || defaultAriaLabel}
          level={1}
          variant="neutral"
        >
          {children}
        </ProfileLink>
      );
    },
    [gettext, linkAriaLabel, logger, profileBaseUrl],
  );

  const TextMarkup = useCallback(
    (children: ReactNode) => {
      if (!sx) {
        return null;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      return <Text sx={sx}>{children}</Text>;
    },
    [sx],
  );

  return (
    <MarkupComponents
      components={{
        highlight: TextMarkup,
        link: LinkMarkup,
      }}
    >
      {label}
    </MarkupComponents>
  );
};
